import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Brain, Activity, AlertTriangle, Leaf, Heart, FileText,
  RefreshCw, ChevronDown, ChevronUp, Sparkles, Stethoscope,
  TestTube2, Download, MapPin, ShieldCheck
} from 'lucide-react';
import { API_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import logger from '../../utils/logger';

const apiCall = async (path, opts = {}) => {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const e = await res.json(); msg = e.detail || msg; } catch (_) { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
};

const RiskCard = ({ name, score, band, icon: Icon }) => {
  const tone = band === 'high' ? 'rose' : band === 'moderate' ? 'amber' : 'emerald';
  return (
    <div
      className={`p-3 rounded-xl border bg-${tone}-50 border-${tone}-200`}
      data-testid={`risk-card-${name}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
          <Icon className={`w-4 h-4 text-${tone}-600`} />
          {name}
        </div>
        <span className={`text-[10px] font-semibold uppercase text-${tone}-700`}>{band}</span>
      </div>
      <div className="flex items-end gap-1.5">
        <span className={`text-2xl font-bold text-${tone}-700`}>{score}</span>
        <span className="text-xs text-gray-500 mb-1">/ 100</span>
      </div>
      <div className="mt-2 h-1.5 bg-white rounded-full overflow-hidden">
        <div className={`h-full bg-${tone}-500 transition-all`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
};

const Section = ({ id, title, icon: Icon, expanded, onToggle, children, count }) => (
  <Card className="mb-3" data-testid={`section-${id}`}>
    <CardHeader
      className="cursor-pointer hover:bg-gray-50 transition py-3"
      onClick={onToggle}
    >
      <div className="flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className="w-4 h-4 text-orange-600" />
          {title}
          {typeof count === 'number' && (
            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
              {count}
            </span>
          )}
        </CardTitle>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </div>
    </CardHeader>
    {expanded && <CardContent className="pt-0">{children}</CardContent>}
  </Card>
);

const AIAnalyticsDashboard = ({ patientId }) => {
  const { user } = useAuth();
  const [analyzing, setAnalyzing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({
    profile: true, risk: true, summary: true,
    diffs: true, labs: true, specialists: true, ayurveda: true, action: true,
  });

  const toggle = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  const runAnalysis = useCallback(async () => {
    try {
      setError(null);
      setAnalyzing(true);
      const data = await apiCall('/api/ai/health-intelligence', {
        method: 'POST',
        body: JSON.stringify({
          email: user?.email,
          location: user?.city ? { city: user.city } : null,
          include_ayurveda: true,
          include_specialists: true,
          include_lab_recommendations: true,
        }),
      });
      setReport(data);
    } catch (e) {
      logger.error('AI analysis failed', e);
      setError(e.message || 'Analysis failed.');
    } finally {
      setAnalyzing(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.email) runAnalysis();
  }, [user?.email, runAnalysis]);

  const downloadPdf = async () => {
    try {
      setDownloading(true);
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/ai/health-intelligence/pdf`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          email: user?.email,
          include_ayurveda: true,
          include_specialists: true,
          include_lab_recommendations: true,
        }),
      });
      if (!res.ok) throw new Error(`PDF generation failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `infuse-health-report-${(report?.report_id || 'latest').slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      logger.error('PDF download failed', e);
      alert('Could not download PDF: ' + e.message);
    } finally {
      setDownloading(false);
    }
  };

  if (analyzing && !report) {
    return (
      <Card>
        <CardContent className="pt-8 pb-8 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto mb-3" />
          <p className="text-sm text-gray-600">Running multi-source AI analysis…</p>
          <p className="text-xs text-gray-400 mt-1">Fusing wearables, labs, prescriptions and your profile.</p>
        </CardContent>
      </Card>
    );
  }

  if (error && !report) {
    return (
      <Card data-testid="ai-analysis-error">
        <CardContent className="pt-6 text-center">
          <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto mb-2" />
          <p className="text-sm text-gray-700 mb-3">{error}</p>
          <Button onClick={runAnalysis} data-testid="retry-analysis">
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card>
        <CardContent className="pt-6 text-center">
          <Sparkles className="w-10 h-10 text-orange-500 mx-auto mb-2" />
          <p className="text-sm text-gray-700 mb-3">
            Tap below to run a fresh multi-source AI analysis on your data.
          </p>
          <Button onClick={runAnalysis} data-testid="run-analysis">
            <Brain className="w-4 h-4 mr-2" /> Run Analysis
          </Button>
        </CardContent>
      </Card>
    );
  }

  const pm = report.patient_meta || {};
  const risk = report.risk_scores || {};

  return (
    <div data-testid="ai-analytics-dashboard">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-xs text-gray-500">
          Engine: <span className="font-mono">{report.engine_version}</span> · Generated {new Date(report.generated_at).toLocaleString()}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={runAnalysis} disabled={analyzing} data-testid="refresh-analysis">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${analyzing ? 'animate-spin' : ''}`} />
            {analyzing ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button size="sm" onClick={downloadPdf} disabled={downloading} data-testid="download-pdf"
                  className="bg-orange-600 hover:bg-orange-700">
            <Download className={`w-4 h-4 mr-1.5 ${downloading ? 'animate-pulse' : ''}`} />
            {downloading ? 'Generating…' : 'Health PDF'}
          </Button>
        </div>
      </div>

      {/* Profile-used banner */}
      <Section id="profile" title="Profile used by the AI engine"
               icon={ShieldCheck}
               expanded={expanded.profile} onToggle={() => toggle('profile')}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {[
            ['Age', pm.age ?? '—'],
            ['Sex', pm.gender ?? '—'],
            ['Blood', pm.blood_group ?? '—'],
            ['Weight', pm.weight_kg ? `${pm.weight_kg} kg` : '—'],
            ['Height', pm.height_cm ? `${pm.height_cm} cm` : '—'],
            ['BMI', pm.bmi ?? '—'],
            ['Wearable readings', report.data_sources?.wearable_readings ?? 0],
            ['Lab reports', report.data_sources?.lab_reports ?? 0],
          ].map(([label, value]) => (
            <div key={label} className="bg-orange-50 border border-orange-100 rounded p-2">
              <div className="text-[10px] uppercase text-orange-700">{label}</div>
              <div className="font-semibold text-gray-900">{String(value)}</div>
            </div>
          ))}
        </div>
        {(report.demographic_correlation?.missing_inputs || []).length > 0 && (
          <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded p-2">
            Missing inputs reduce confidence: {report.demographic_correlation.missing_inputs.join(', ')}.
            Update your profile to improve accuracy.
          </p>
        )}
      </Section>

      {/* Risk scores */}
      <Section id="risk" title="Body-system risk scores" icon={Activity}
               expanded={expanded.risk} onToggle={() => toggle('risk')}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <RiskCard name="cardiovascular" {...risk.cardiovascular} icon={Heart} />
          <RiskCard name="metabolic"      {...risk.metabolic}      icon={Activity} />
          <RiskCard name="renal"          {...risk.renal}          icon={Activity} />
          <RiskCard name="hepatic"        {...risk.hepatic}        icon={Activity} />
          <RiskCard name="mental"         {...risk.mental}         icon={Brain} />
        </div>
      </Section>

      {/* Executive summary */}
      {report.executive_summary && (
        <Section id="summary" title="Executive summary" icon={FileText}
                 expanded={expanded.summary} onToggle={() => toggle('summary')}>
          <p className="text-sm text-gray-700 leading-relaxed">{report.executive_summary}</p>
        </Section>
      )}

      {/* Differential diagnoses */}
      {(report.differential_diagnoses || []).length > 0 && (
        <Section id="diffs" title="Differential diagnoses" icon={Brain}
                 count={(report.differential_diagnoses || []).length}
                 expanded={expanded.diffs} onToggle={() => toggle('diffs')}>
          <div className="space-y-2">
            {report.differential_diagnoses.slice(0, 6).map((d, i) => (
              <div key={i} className="border-l-4 border-orange-400 pl-3 py-1">
                <div className="font-semibold text-sm text-gray-900">{d.condition}</div>
                <div className="text-[11px] text-gray-500 capitalize">likelihood: {d.likelihood}</div>
                {(d.supporting_evidence || []).length > 0 && (
                  <div className="text-xs text-gray-600 mt-1">
                    Evidence: {d.supporting_evidence.slice(0, 3).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Recommended lab tests */}
      {(report.recommended_lab_tests || []).length > 0 && (
        <Section id="labs" title="Recommended lab tests" icon={TestTube2}
                 count={report.recommended_lab_tests.length}
                 expanded={expanded.labs} onToggle={() => toggle('labs')}>
          <div className="space-y-2">
            {report.recommended_lab_tests.map((lt, i) => (
              <div key={i}
                   className="flex items-center justify-between gap-2 border rounded-lg p-2 bg-gray-50"
                   data-testid={`lab-reco-${i}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm text-gray-900 truncate">{lt.test_name}</div>
                  <div className="text-[11px] text-gray-500">
                    {(lt.urgency || 'routine').replace(/_/g, ' ')}
                    {lt.price_inr ? ` · ₹${lt.price_inr}` : ''}
                    {lt.fasting_required ? ' · fasting' : ''}
                  </div>
                  {lt.rationale && <div className="text-[11px] text-gray-600 mt-0.5">{lt.rationale}</div>}
                </div>
                {lt.test_id ? (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0">Book</Button>
                ) : (
                  <span className="text-[10px] text-gray-500 flex-shrink-0">External</span>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Specialists */}
      {(report.specialist_recommendations || []).length > 0 && (
        <Section id="specialists" title="Suggested specialists" icon={Stethoscope}
                 count={report.specialist_recommendations.length}
                 expanded={expanded.specialists} onToggle={() => toggle('specialists')}>
          <div className="space-y-3">
            {report.specialist_recommendations.map((sb, i) => (
              <div key={i} className="border rounded-lg p-2.5 bg-white">
                <div className="text-xs font-semibold text-gray-900 mb-1">{sb.trigger_condition}</div>
                <div className="grid md:grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-orange-600 font-medium">Allopathic — {sb.allopathic_specialty}</div>
                    {(sb.allopathic_doctors || []).length === 0 ? (
                      <div className="text-gray-500">No partner doctor in your area yet.</div>
                    ) : sb.allopathic_doctors.slice(0, 2).map((d, j) => (
                      <div key={j} className="text-gray-700">
                        • {d.name} <span className="text-gray-400">— {d.city}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-emerald-600 font-medium">Ayurvedic — {sb.ayurvedic_specialty}</div>
                    {(sb.ayurvedic_doctors || []).length === 0 ? (
                      <div className="text-gray-500">No partner Vaidya in your area yet.</div>
                    ) : sb.ayurvedic_doctors.slice(0, 2).map((d, j) => (
                      <div key={j} className="text-gray-700">
                        • {d.name} <span className="text-gray-400">— {d.city}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Ayurvedic alternative care */}
      {report.ayurvedic_alternative_care && (
        <Section id="ayurveda" title="Ayurveda — Vata · Pitta · Kapha" icon={Leaf}
                 expanded={expanded.ayurveda} onToggle={() => toggle('ayurveda')}>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 mb-2">
            <div className="text-xs font-semibold text-emerald-900 mb-0.5">Dosha imbalance</div>
            <div className="text-base font-bold text-emerald-700">
              {report.ayurvedic_alternative_care.dosha_imbalance}
            </div>
            {report.ayurvedic_alternative_care.rationale && (
              <p className="text-xs text-gray-700 mt-1">
                {report.ayurvedic_alternative_care.rationale}
              </p>
            )}
          </div>

          <div className="text-xs font-semibold text-gray-800 mb-1">Recommended herbs</div>
          <div className="space-y-1.5 mb-3">
            {(report.ayurvedic_alternative_care.herb_suggestions || []).map((h, i) => (
              <div key={i} className="border rounded p-2 text-xs bg-white" data-testid={`herb-${i}`}>
                <div className="font-semibold text-emerald-700">{h.herb}</div>
                <div className="text-gray-700">Use: {h.use}</div>
                <div className="text-gray-600">Form: {h.typical_form}</div>
                <div className="text-amber-700 text-[11px]">Caution: {h.caution}</div>
              </div>
            ))}
          </div>

          <div className="text-xs font-semibold text-gray-800 mb-1">Lifestyle</div>
          <ul className="text-xs text-gray-700 list-disc pl-4 space-y-1">
            {(report.ayurvedic_alternative_care.lifestyle_interventions || []).map((li, i) => (
              <li key={i}><b>{li.intervention}:</b> {li.detail}</li>
            ))}
          </ul>

          {(report.ayurvedic_alternative_care.panchakarma_therapies || []).length > 0 && (
            <p className="text-xs text-gray-700 mt-2">
              <b>Panchakarma:</b> {report.ayurvedic_alternative_care.panchakarma_therapies.join(', ')}
            </p>
          )}
          <p className="text-[10px] text-gray-500 mt-2 italic">
            {report.ayurvedic_alternative_care.disclaimer}
          </p>
        </Section>
      )}

      {/* Next best action */}
      {report.next_best_action && (
        <Section id="action" title="Next best action" icon={MapPin}
                 expanded={expanded.action} onToggle={() => toggle('action')}>
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-3">
            <div className="text-base font-bold text-orange-700">
              {report.next_best_action.label}
            </div>
            <div className="text-[11px] text-orange-600 uppercase font-semibold mb-1">
              Urgency: {report.next_best_action.urgency}
            </div>
            <p className="text-sm text-gray-700">{report.next_best_action.rationale}</p>
          </div>
        </Section>
      )}

      <p className="text-[10px] text-gray-500 italic mt-2 text-center">
        {report.disclaimer}
      </p>
    </div>
  );
};

export default AIAnalyticsDashboard;
